import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Lock,
  RotateCcw,
  Sparkles,
} from "lucide-react";

/**
 * Multi-step audit funnel.
 * Stages:
 *   "form"    → steps 1..5
 *   "loading" → backend audit submission in progress
 *   "success" → safe confirmation after backend receipt
 *   "error"   → friendly retry state
 *   "preview" → example report preview
 */

const BUSINESS_TYPES = [
  "Professional services",
  "Healthcare or clinics",
  "Property or real estate",
  "Recruitment or staffing",
  "Finance or insurance",
  "Ecommerce or retail",
  "SaaS or technology",
  "Operations-heavy business",
  "Education or training",
  "Other",
];

const PRIORITIES = [
  "A higher-converting website",
  "More qualified leads",
  "Better SEO and AI-search visibility",
  "Automating repetitive workflows",
  "An AI voice agent for calls or follow-up",
  "A customer-facing web application",
  "An internal operational tool",
  "Turning a process into a SaaS product",
  "Not sure yet, I need guidance",
];

const BOTTLENECKS = [
  "Our website is outdated or underperforming",
  "Lead follow-up is slow or inconsistent",
  "Too much work is handled manually",
  "Calls or enquiries are not handled efficiently",
  "Our systems do not connect properly",
  "We need a product built but do not know where to begin",
  "Competitors appear more digitally advanced",
  "We do not know where AI would help most",
];

const OUTCOMES = [
  "Quick website or funnel improvement",
  "SEO and AI-visibility improvement",
  "AI automation pilot",
  "AI voice workflow",
  "Full web application",
  "Internal operational platform",
  "Long-term AI systems roadmap",
  "Vertical SaaS concept validation",
  "Just exploring opportunities",
];

const INITIAL_DATA = {
  firstName: "",
  email: "",
  companyName: "",
  websiteUrl: "",
  hasWebsite: true,
  phone: "",
  businessType: "",
  primaryPriority: "",
  biggestBottleneck: "",
  intendedOutcome: "",
  additionalContext: "",
};

const TOTAL_STEPS = 5;

const STEP_LABELS = [
  "What best describes your business?",
  "What would create the most value right now?",
  "Where is the biggest bottleneck today?",
  "What kind of outcome are you considering?",
  "Where should we send your personalized audit?",
];

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

// --- validation helpers ---
const EMAIL_RX = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
const URL_RX =
  /^https?:\/\/([A-Za-z0-9-]+\.)+[A-Za-z]{2,}(\/[\w\-.~:/?#[\]@!$&'()*+,;=%]*)?$/i;

function validateContactStep(d) {
  const e = {};
  if (!d.firstName.trim()) e.firstName = "Please enter your first name.";
  if (!d.email.trim()) e.email = "Please enter your work email.";
  else if (!EMAIL_RX.test(d.email.trim()))
    e.email = "Please enter a valid email address.";
  if (!d.companyName.trim()) e.companyName = "Please enter your company name.";
  if (d.hasWebsite && d.websiteUrl.trim()) {
    if (!URL_RX.test(d.websiteUrl.trim()))
      e.websiteUrl = "Please enter a valid URL beginning with http(s)://";
  }
  return e;
}

// --- small UI primitives ---
function ProgressBar({ step }) {
  const pct = (step / TOTAL_STEPS) * 100;
  return (
    <div
      className="audit-progress"
      role="progressbar"
      aria-valuemin={1}
      aria-valuemax={TOTAL_STEPS}
      aria-valuenow={step}
      data-testid="audit-progress"
    >
      <div className="audit-progress-meta">
        <span
          className="font-mono"
          style={{
            color: "var(--accent-soft)",
            fontSize: 11,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
          }}
        >
          Step {step} of {TOTAL_STEPS}
        </span>
        <span
          className="font-mono"
          style={{
            color: "var(--silver)",
            fontSize: 11,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
          }}
        >
          {STEP_LABELS[step - 1]}
        </span>
      </div>
      <div className="audit-progress-track">
        <div
          className="audit-progress-fill"
          style={{ width: `${pct}%` }}
          data-testid="audit-progress-fill"
        />
      </div>
    </div>
  );
}

function TextField({
  id,
  label,
  required,
  helper,
  error,
  disabled,
  value,
  onChange,
  type = "text",
  placeholder,
  testId,
}) {
  return (
    <div className="field-block">
      <label htmlFor={id} className="field-label">
        {label}
        {required ? <span style={{ color: "var(--accent-soft)" }}> *</span> : null}
      </label>
      <input
        id={id}
        name={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${id}-err` : helper ? `${id}-helper` : undefined}
        className={`field-input${error ? " has-error" : ""}${disabled ? " is-disabled" : ""}`}
        data-testid={testId}
      />
      {error ? (
        <div id={`${id}-err`} className="field-error" data-testid={`${testId}-error`}>
          {error}
        </div>
      ) : helper ? (
        <div id={`${id}-helper`} className="field-helper">
          {helper}
        </div>
      ) : null}
    </div>
  );
}

function OptionList({ options, value, onChange, name, testIdPrefix }) {
  return (
    <div className="option-list" role="radiogroup" aria-label={name}>
      {options.map((opt, i) => {
        const active = value === opt;
        return (
          <button
            key={opt}
            type="button"
            role="radio"
            aria-checked={active}
            className={`option-tile${active ? " is-active" : ""}`}
            onClick={() => onChange(opt)}
            data-testid={`${testIdPrefix}-${i}`}
          >
            <span className="option-dot" aria-hidden="true">
              <span />
            </span>
            <span className="option-text">{opt}</span>
          </button>
        );
      })}
    </div>
  );
}

// --- step renderers ---
function ContactStep({ data, set, errors }) {
  const websiteHelper = data.hasWebsite
    ? data.websiteUrl.trim()
      ? "Your audit can be grounded in publicly visible content from your website."
      : "Add your website so ForgeLayer can review publicly visible pages alongside your answers."
    : "No website yet? Your audit can still identify what to build, automate or improve first.";

  return (
    <div className="step-block" data-testid="audit-contact-step">
      <p className="step-supporting">
        Submit your answers to receive your personalized ForgeLab audit. Your website is optional, and helps ground the audit in public digital presence signals.
      </p>

      <div className="field-grid two">
        <TextField
          id="firstName"
          label="First name"
          required
          value={data.firstName}
          onChange={(v) => set({ firstName: v })}
          error={errors.firstName}
          placeholder="Enter your first name"
          testId="audit-firstName"
        />
        <TextField
          id="email"
          label="Work email"
          required
          type="email"
          value={data.email}
          onChange={(v) => set({ email: v })}
          error={errors.email}
          placeholder="you@company.com"
          testId="audit-email"
        />
      </div>

      <div className="field-grid two">
        <TextField
          id="companyName"
          label="Company name"
          required
          value={data.companyName}
          onChange={(v) => set({ companyName: v })}
          error={errors.companyName}
          placeholder="Your company name"
          testId="audit-company"
        />
        <TextField
          id="phone"
          label="Phone number"
          value={data.phone}
          onChange={(v) => set({ phone: v })}
          placeholder="Your phone number"
          helper="Optional."
          testId="audit-phone"
        />
      </div>

      <TextField
        id="websiteUrl"
        label="Website URL"
        type="url"
        value={data.websiteUrl}
        onChange={(v) => set({ websiteUrl: v })}
        error={errors.websiteUrl}
        placeholder="https://yourcompany.com"
        disabled={!data.hasWebsite}
        helper={websiteHelper}
        testId="audit-websiteUrl"
      />

      <label
        className="checkbox-block"
        data-testid="audit-no-website-toggle"
      >
        <input
          type="checkbox"
          checked={!data.hasWebsite}
          onChange={(e) =>
            set({
              hasWebsite: !e.target.checked,
              websiteUrl: e.target.checked ? "" : data.websiteUrl,
            })
          }
          aria-label="I don't currently have a website"
        />
        <span className="checkbox-mark" aria-hidden="true">
          <Check size={12} strokeWidth={3} />
        </span>
        <span className="checkbox-text">I don’t currently have a website</span>
      </label>
    </div>
  );
}

function StepOptions({ options, value, set, fieldKey, supporting, testIdPrefix, stepId }) {
  return (
    <div className="step-block" data-testid={`audit-step-${stepId}`}>
      {supporting ? <p className="step-supporting">{supporting}</p> : null}
      <OptionList
        options={options}
        value={value}
        onChange={(v) => set({ [fieldKey]: v })}
        name={fieldKey}
        testIdPrefix={testIdPrefix}
      />
    </div>
  );
}

function OutcomeStep({ data, set }) {
  return (
    <div className="step-block" data-testid="audit-step-4">
      <OptionList
        options={OUTCOMES}
        value={data.intendedOutcome}
        onChange={(v) => set({ intendedOutcome: v })}
        name="intendedOutcome"
        testIdPrefix="audit-outcome"
      />

      <div className="field-block" style={{ marginTop: 28 }}>
        <label htmlFor="additionalContext" className="field-label">
          Anything specific you want ForgeLayer to assess?
        </label>
        <textarea
          id="additionalContext"
          name="additionalContext"
          rows={4}
          value={data.additionalContext}
          onChange={(e) => set({ additionalContext: e.target.value })}
          placeholder="Tell us about an existing workflow, website concern, idea or business problem."
          className="field-input"
          data-testid="audit-additionalContext"
          style={{ resize: "vertical", minHeight: 110, fontFamily: "Manrope, sans-serif" }}
        />
        <div className="field-helper">Optional.</div>
      </div>
    </div>
  );
}

// --- submit states ---
function SubmitState({ kind, error, onPreview, onReset, onRetry }) {
  const isLoading = kind === "loading";
  const isSuccess = kind === "success";
  return (
    <div className="audit-card" data-testid={`audit-submit-${kind}`}>
      <div className="preview-badge" data-testid="audit-submit-badge">
        <Sparkles size={11} strokeWidth={2} />
        {isLoading ? "Preparing Audit" : isSuccess ? "Audit Received" : "Submission Issue"}
      </div>
      <h3 className="font-display" style={{ color: "#fff", fontSize: "clamp(28px, 3.4vw, 40px)", fontWeight: 500, letterSpacing: "-0.02em", margin: "20px 0 10px", lineHeight: 1.1 }} data-testid="audit-submit-headline">
        {isLoading ? "Preparing your personalized ForgeLab audit…" : isSuccess ? "Your audit is being generated and will be emailed to you." : "We couldn’t submit your audit just now."}
      </h3>
      <p style={{ color: "var(--ink-soft)", fontSize: 16, lineHeight: 1.6, maxWidth: 640, marginTop: 0 }} data-testid="audit-submit-body">
        {isLoading ? "Securely sending your answers to ForgeLayer." : isSuccess ? "ForgeLayer has received your answers and will use them to prepare your personalized ForgeLab strategy audit." : (error || "Please try again. If the issue continues, contact ForgeLayer directly.")}
      </p>
      {!isLoading ? (
        <div className="audit-actions" style={{ marginTop: 28 }}>
          {isSuccess ? <button type="button" onClick={onPreview} className="btn-primary" data-testid="audit-view-preview">View Example Audit Preview<span className="btn-arrow" aria-hidden="true"><ArrowRight size={13} strokeWidth={2.2} /></span></button> : <button type="button" onClick={onRetry} className="btn-primary" data-testid="audit-retry-submit">Try Again<span className="btn-arrow" aria-hidden="true"><ArrowRight size={13} strokeWidth={2.2} /></span></button>}
          <button type="button" onClick={onReset} className="btn-ghost" data-testid="audit-start-over"><RotateCcw size={14} strokeWidth={1.8} /><span style={{ marginLeft: 6 }}>Start Over</span></button>
        </div>
      ) : null}
    </div>
  );
}

// --- sample preview state ---
const SAMPLE_CATEGORIES = [
  { label: "Website Conversion Readiness", score: 64 },
  { label: "SEO & AI Visibility", score: 52 },
  { label: "Workflow Automation Potential", score: 84 },
  { label: "Voice Agent Suitability", score: 76 },
  { label: "Product / SaaS Opportunity", score: 69 },
];

const SAMPLE_FINDINGS = [
  {
    title: "Website Conversion Path",
    body:
      "Example observation: the website may benefit from a clearer assessment-led conversion journey rather than relying only on a generic contact form.",
    tag: "Website Opportunity",
  },
  {
    title: "AI Visibility Structure",
    body:
      "Example observation: structured service content and clearer topical pages may improve discoverability across traditional search and AI-driven discovery.",
    tag: "SEO + GEO Opportunity",
  },
  {
    title: "Workflow Automation",
    body:
      "Example opportunity: automate lead intake, qualification, routing and follow-up to reduce manual handling.",
    tag: "Automation Opportunity",
  },
  {
    title: "Voice Workflow",
    body:
      "Example opportunity: evaluate an AI voice agent for enquiry handling, lead qualification or follow-up workflows.",
    tag: "Voice Opportunity",
  },
];

const EVIDENCE_LEGEND = [
  {
    label: "Observed on website",
    body: "Directly supported by publicly visible website content.",
    tone: "blue",
  },
  {
    label: "Provided by prospect",
    body: "Based on answers submitted during the audit.",
    tone: "soft",
  },
  {
    label: "Recommended opportunity",
    body: "A proposed improvement or system worth evaluating.",
    tone: "neutral",
  },
  {
    label: "Not verifiable",
    body: "Information that cannot be confirmed from the available evidence.",
    tone: "muted",
  },
];

const DECK_SLIDES = [
  { n: "01", title: "Business Snapshot" },
  { n: "02", title: "Visible Digital Position" },
  { n: "03", title: "Website Conversion Opportunities" },
  { n: "04", title: "SEO & AI Visibility Opportunities" },
  { n: "05", title: "Automation Workflow Opportunities" },
  { n: "06", title: "Voice Agent Suitability" },
  { n: "07", title: "Recommended Build Roadmap" },
  { n: "08", title: "Next-Step Engagement" },
];

function ScoreBar({ score }) {
  return (
    <div className="score-bar" aria-hidden="true">
      <div
        className="score-bar-fill"
        style={{ width: `${Math.max(0, Math.min(100, score))}%` }}
      />
    </div>
  );
}

function SamplePreview({ onReturn }) {
  return (
    <div className="audit-card sample-preview" data-testid="audit-sample-preview">
      <div className="preview-badge" data-testid="audit-sample-badge">
        <Sparkles size={11} strokeWidth={2} />
        Sample Preview — Example Output Only
      </div>
      <p
        style={{
          color: "var(--silver)",
          fontSize: 13,
          margin: "14px 0 0",
          maxWidth: 720,
        }}
        data-testid="audit-sample-notice"
      >
        This preview illustrates the type of ForgeLab report ForgeLayer generates. It is not based on your submitted website or answers.
      </p>

      {/* A. Opportunity score card */}
      <div className="opp-score" data-testid="audit-opp-score">
        <div>
          <div className="eyebrow" style={{ marginBottom: 8 }}>
            AI Growth Opportunity Score
          </div>
          <div
            className="font-display"
            style={{
              fontSize: "clamp(54px, 7vw, 92px)",
              color: "#fff",
              fontWeight: 500,
              letterSpacing: "-0.04em",
              lineHeight: 1,
            }}
          >
            78<span style={{ color: "var(--silver)", fontWeight: 400 }}> / 100</span>
          </div>
          <div
            className="font-display"
            style={{
              color: "var(--accent-soft)",
              fontSize: 15,
              marginTop: 8,
              letterSpacing: "-0.01em",
            }}
          >
            High-Leverage Opportunity
          </div>
        </div>
        <div
          style={{
            color: "var(--ink-soft)",
            fontSize: 14,
            lineHeight: 1.6,
            maxWidth: 360,
          }}
        >
          Example only: significant potential across digital conversion,
          automation and AI-enabled customer interaction.
        </div>
      </div>

      {/* B. Diagnostic category scores */}
      <section className="sample-block" data-testid="audit-categories">
        <div className="sample-block-header">
          <div className="eyebrow">Diagnostic categories</div>
          <span className="sample-block-tag">Sample data</span>
        </div>
        <div className="category-list">
          {SAMPLE_CATEGORIES.map((c, i) => (
            <div
              key={c.label}
              className="category-row"
              data-testid={`audit-category-${i}`}
            >
              <div className="category-label">{c.label}</div>
              <ScoreBar score={c.score} />
              <div className="category-score">
                <span
                  className="font-display"
                  style={{ color: "#fff", fontWeight: 500 }}
                >
                  {c.score}
                </span>
                <span style={{ color: "var(--silver)" }}>/100</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* C. Findings */}
      <section className="sample-block" data-testid="audit-findings">
        <div className="sample-block-header">
          <div className="eyebrow">Example Findings</div>
          <span className="sample-block-tag">Illustrative</span>
        </div>
        <div className="findings-grid">
          {SAMPLE_FINDINGS.map((f, i) => (
            <article
              key={f.title}
              className="finding-card"
              data-testid={`audit-finding-${i}`}
            >
              <span className="finding-tag">{f.tag}</span>
              <h4>{f.title}</h4>
              <p>{f.body}</p>
            </article>
          ))}
        </div>
      </section>

      {/* D. Evidence source legend */}
      <section className="sample-block" data-testid="audit-evidence-legend">
        <div className="sample-block-header">
          <div className="eyebrow">Evidence sources</div>
          <span className="sample-block-tag">How statements are classified</span>
        </div>
        <div className="legend-grid">
          {EVIDENCE_LEGEND.map((e, i) => (
            <div
              key={e.label}
              className={`legend-row tone-${e.tone}`}
              data-testid={`audit-legend-${i}`}
            >
              <div className="legend-label">{e.label}</div>
              <div className="legend-body">{e.body}</div>
            </div>
          ))}
        </div>
      </section>

      {/* E. Strategy deck preview */}
      <section className="sample-block" data-testid="audit-deck-preview">
        <div className="sample-block-header">
          <div>
            <div className="eyebrow">Your ForgeLayer AI Strategy Deck</div>
            <p
              style={{
                color: "var(--silver)",
                fontSize: 13,
                margin: "6px 0 0",
                maxWidth: 540,
              }}
            >
              An evidence-led roadmap covering immediate improvements,
              recommended AI systems and phased build opportunities.
            </p>
          </div>
        </div>
        <div className="deck-grid">
          {DECK_SLIDES.map((s, i) => (
            <div key={s.n} className="deck-tile" data-testid={`audit-deck-slide-${i}`}>
              <span className="deck-tile-n font-mono">{s.n}</span>
              <span className="deck-tile-title">{s.title}</span>
              <span className="deck-tile-shape" aria-hidden="true" />
            </div>
          ))}
        </div>
        <button
          type="button"
          disabled
          aria-disabled="true"
          className="btn-ghost"
          style={{ marginTop: 22, opacity: 0.6, cursor: "not-allowed" }}
          data-testid="audit-deck-disabled-btn"
        >
          <Lock size={13} strokeWidth={2} />
          <span style={{ marginLeft: 8 }}>
            Personalized Deck Delivered By Email
          </span>
        </button>
      </section>

      {/* F. Preview CTA */}
      <section className="sample-block preview-cta" data-testid="audit-preview-cta">
        <h4
          className="font-display"
          style={{
            color: "#fff",
            fontSize: "clamp(24px, 3vw, 32px)",
            fontWeight: 500,
            letterSpacing: "-0.02em",
            margin: 0,
          }}
        >
          Ready to turn the right opportunity into a working system?
        </h4>
        <p
          style={{
            color: "var(--ink-soft)",
            fontSize: 15,
            lineHeight: 1.6,
            margin: "10px 0 22px",
            maxWidth: 560,
          }}
        >
          ForgeLayer can help define what to improve, automate or build first.
        </p>
        <div className="audit-actions">
          <button
            type="button"
            onClick={onReturn}
            className="btn-ghost"
            data-testid="audit-return-to-audit"
          >
            <ArrowLeft size={14} strokeWidth={1.8} />
            <span style={{ marginLeft: 6 }}>Return to Audit</span>
          </button>
          <a
            href="#services"
            className="btn-primary"
            data-testid="audit-explore-build"
          >
            Explore What We Build
            <span className="btn-arrow" aria-hidden="true">
              <ArrowRight size={13} strokeWidth={2.2} />
            </span>
          </a>
        </div>
      </section>
    </div>
  );
}

// --- main funnel ---
export default function AuditFunnel() {
  const [stage, setStage] = useState("form");
  const [step, setStep] = useState(1);
  const [data, setData] = useState(INITIAL_DATA);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const cardRef = useRef(null);

  const set = (patch) => setData((d) => ({ ...d, ...patch }));

  // smooth focus shift on stage / step changes
  useEffect(() => {
    if (cardRef.current && (stage !== "form" || step > 1)) {
      const top = cardRef.current.getBoundingClientRect().top + window.scrollY - 90;
      window.scrollTo({ top, behavior: "smooth" });
    }
    // intentionally not depending on cardRef
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, step]);

  const canContinue = useMemo(() => {
    if (step === 1) return Boolean(data.businessType);
    if (step === 2) return Boolean(data.primaryPriority);
    if (step === 3) return Boolean(data.biggestBottleneck);
    if (step === 4) return Boolean(data.intendedOutcome);
    if (step === 5) return Object.keys(validateContactStep(data)).length === 0;
    return false;
  }, [step, data]);

  const submitAudit = async () => {
    const e = validateContactStep(data);
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    setSubmitError("");
    setStage("loading");
    try {
      const response = await fetch(`${API_BASE}/api/forgelab/audit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: data.firstName.trim(),
          work_email: data.email.trim(),
          company_name: data.companyName.trim(),
          phone_number: data.phone.trim() || null,
          website_url: data.hasWebsite ? data.websiteUrl.trim() || null : null,
          has_no_website: !data.hasWebsite,
          answers: {
            business_type: data.businessType,
            primary_priority: data.primaryPriority,
            biggest_bottleneck: data.biggestBottleneck,
            intended_outcome: data.intendedOutcome,
            additional_context: data.additionalContext.trim(),
          },
          source: "interactive_audit",
        }),
      });
      if (!response.ok) throw new Error("The audit endpoint returned an error.");
      setStage("success");
    } catch (error) {
      setSubmitError("Something went wrong while submitting your audit. Please try again in a moment.");
      setStage("error");
    }
  };

  const handleNext = () => {
    if (step < TOTAL_STEPS) {
      setErrors({});
      setSubmitError("");
      setStep(step + 1);
    } else {
      submitAudit();
    }
  };

  const handleBack = () => {
    setErrors({});
    if (step > 1) setStep(step - 1);
  };

  const handleReset = () => {
    setStage("form");
    setStep(1);
    setData(INITIAL_DATA);
    setErrors({});
    setSubmitError("");
  };

  const isFinal = step === TOTAL_STEPS;

  return (
    <section
      id="audit-form"
      className="section"
      style={{
        paddingTop: 0,
        paddingBottom: "clamp(80px, 12vw, 160px)",
      }}
      data-testid="audit-funnel-section"
    >
      <div className="container-edge">
        <div ref={cardRef} />

        {stage === "form" && (
          <div className="audit-card" data-testid="audit-card">
            <div className="audit-card-head">
              <div>
                <div className="eyebrow" style={{ marginBottom: 8 }}>
                  Free AI Website &amp; Growth Systems Audit
                </div>
                <p
                  style={{
                    color: "var(--ink-soft)",
                    fontSize: 14,
                    margin: 0,
                    maxWidth: 560,
                    lineHeight: 1.55,
                  }}
                >
                  Answer a few questions first, then submit your details to receive a personalized ForgeLab strategy audit.
                </p>
              </div>
              <ProgressBar step={step} />
            </div>

            <div className="audit-step-stage" aria-live="polite">
              <h3
                className="font-display step-title"
                key={`title-${step}`}
                data-testid={`audit-step-title-${step}`}
              >
                {STEP_LABELS[step - 1]}
              </h3>

              <div key={`step-${step}`} className="step-animate">
                {step === 1 ? (
                  <StepOptions stepId={1} options={BUSINESS_TYPES} value={data.businessType} set={set} fieldKey="businessType" testIdPrefix="audit-business-type" />
                ) : step === 2 ? (
                  <StepOptions stepId={2} options={PRIORITIES} value={data.primaryPriority} set={set} fieldKey="primaryPriority" testIdPrefix="audit-priority" />
                ) : step === 3 ? (
                  <StepOptions stepId={3} options={BOTTLENECKS} value={data.biggestBottleneck} set={set} fieldKey="biggestBottleneck" testIdPrefix="audit-bottleneck" />
                ) : step === 4 ? (
                  <OutcomeStep data={data} set={set} />
                ) : (
                  <ContactStep data={data} set={set} errors={errors} />
                )}
              </div>
            </div>

            <div className="audit-actions" data-testid="audit-actions">
              <button
                type="button"
                onClick={handleBack}
                className="btn-ghost"
                disabled={step === 1}
                style={{
                  opacity: step === 1 ? 0.4 : 1,
                  cursor: step === 1 ? "not-allowed" : "pointer",
                }}
                data-testid="audit-back"
              >
                <ArrowLeft size={14} strokeWidth={1.8} />
                <span style={{ marginLeft: 6 }}>Back</span>
              </button>
              <button
                type="button"
                onClick={handleNext}
                className="btn-primary"
                disabled={!canContinue && step !== TOTAL_STEPS}
                style={{
                  opacity: step === TOTAL_STEPS || canContinue ? 1 : 0.55,
                  cursor:
                    step === TOTAL_STEPS || canContinue ? "pointer" : "not-allowed",
                }}
                data-testid="audit-next"
              >
                {isFinal ? "Submit to Receive My Personalized ForgeLab Audit" : "Continue"}
                <span className="btn-arrow" aria-hidden="true">
                  <ArrowRight size={13} strokeWidth={2.2} />
                </span>
              </button>
            </div>
          </div>
        )}

        {["loading", "success", "error"].includes(stage) && (
          <SubmitState
            kind={stage}
            error={submitError}
            onPreview={() => setStage("preview")}
            onReset={handleReset}
            onRetry={submitAudit}
          />
        )}

        {stage === "preview" && (
          <SamplePreview onReturn={handleReset} />
        )}
      </div>
    </section>
  );
}
